import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
  Badge, Flex, Grid, GridItem, Input, InputGroup, InputLeftElement,
  Icon, Stat, StatLabel, StatNumber, StatHelpText,
  Alert, AlertIcon,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { MdSearch, MdWarning, MdPeople, MdAttachMoney } from 'react-icons/md';
import { getDelinquencyReport } from '../../services/dashboard.service';

const fmt = (v) => new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU' }).format(v || 0);

export default function DelinquencyView() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const cardBg = useColorModeValue('white', 'navy.800');

  const [data, setData] = useState({ accounts: [], totalDebtors: 0, totalDebt: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getDelinquencyReport();
      setData(res?.payload || { accounts: [], totalDebtors: 0, totalDebt: 0 });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const t = search.toLowerCase();
    return (data.accounts || []).filter(a =>
      (a.studentName || a.student_name || '').toLowerCase().includes(t) || a.document?.toLowerCase().includes(t));
  }, [data.accounts, search]);

  if (loading) return null;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {data.totalDebtors > 0 && (
        <Alert status="warning" borderRadius="md" mb={4}>
          <AlertIcon />
          Hay {data.totalDebtors} estudiante(s) con deuda pendiente por un total de {fmt(data.totalDebt)}
        </Alert>
      )}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} mb={6}>
        <GridItem><Card bg={cardBg} p={4}><Stat><StatLabel>Total Deudores</StatLabel><StatNumber color="red.500">{data.totalDebtors}</StatNumber><StatHelpText>Estudiantes con saldo</StatHelpText></Stat></Card></GridItem>
        <GridItem><Card bg={cardBg} p={4}><Stat><StatLabel>Deuda Total</StatLabel><StatNumber color="orange.500">{fmt(data.totalDebt)}</StatNumber><StatHelpText>Monto acumulado</StatHelpText></Stat></Card></GridItem>
        <GridItem><Card bg={cardBg} p={4}><Stat><StatLabel>Deuda Promedio</StatLabel><StatNumber color="yellow.600">{data.totalDebtors > 0 ? fmt(data.totalDebt / data.totalDebtors) : '$0'}</StatNumber><StatHelpText>Por estudiante</StatHelpText></Stat></Card></GridItem>
      </Grid>
      <Card p={4}>
        <Flex mb={4}><InputGroup w="320px"><InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement><Input placeholder="Buscar por nombre o CI..." value={search} onChange={e => setSearch(e.target.value)} /></InputGroup></Flex>
        {filtered.length === 0 ? (
          <Flex justify="center" py={10} direction="column" align="center"><Icon as={MdAttachMoney} w={12} h={12} color="green.300" mb={3} /><Text color="gray.500">No hay deudores</Text></Flex>
        ) : (
          <Box overflowX="auto"><Table size="sm" variant="simple"><Thead><Tr><Th borderColor={borderColor}>Estudiante</Th><Th borderColor={borderColor}>Documento</Th><Th borderColor={borderColor} isNumeric>Saldo Pendiente</Th><Th borderColor={borderColor}>Nivel</Th></Tr></Thead><Tbody>
            {filtered.sort((a, b) => b.balance - a.balance).map((a, i) => (
              <Tr key={i} _hover={{ bg: rowHover }}>
                <Td borderColor={borderColor}><Text fontWeight="600" fontSize="sm">{a.studentName || a.student_name || 'Sin nombre'}</Text></Td>
                <Td borderColor={borderColor}><Text fontSize="sm">{a.document}</Text></Td>
                <Td borderColor={borderColor} isNumeric><Text fontWeight="700" color="red.500">{fmt(a.balance)}</Text></Td>
                <Td borderColor={borderColor}>{Number(a.balance) > 50000 ? <Badge colorScheme="red">Alto</Badge> : Number(a.balance) > 20000 ? <Badge colorScheme="orange">Medio</Badge> : <Badge colorScheme="yellow">Bajo</Badge>}</Td>
              </Tr>
            ))}
          </Tbody></Table></Box>
        )}
      </Card>
    </Box>
  );
}
